import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScienceIcon from '@mui/icons-material/Science';

export interface FeatureAnnouncement {
  id: string;
  title: string;
  description: string;
  linkText?: string;
  linkUrl?: string;
  badge?: string;
  badgeColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  version?: string; // バージョン表記（例: "v1.0.0", "2025.1"）
}

interface FeatureAnnouncementDialogProps {
  announcement: FeatureAnnouncement;
  storageKey?: string; // LocalStorageに保存するキー（指定すると「次回から表示しない」機能が有効になる）
  isEarlyAccess?: boolean; // アーリーアクセスの場合、アイコン・カラーを変更
  onClose?: () => void;
  onNavigate?: () => void; // カスタムナビゲーション処理（指定された場合はlinkUrlよりも優先）
}

const FeatureAnnouncementDialog: React.FC<FeatureAnnouncementDialogProps> = ({
  announcement,
  storageKey,
  isEarlyAccess = false,
  onClose,
  onNavigate
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (storageKey) {
      const dismissedId = localStorage.getItem(storageKey);
      if (dismissedId !== announcement.id) {
        setOpen(true);
      }
    } else {
      setOpen(true);
    }
  }, [storageKey, announcement.id]);

  const handleClose = () => {
    // 閉じたら常に既読として保存（チェックボックス不要）
    if (storageKey) {
      localStorage.setItem(storageKey, announcement.id);
    }
    setOpen(false);
    onClose?.();
  };

  const handleNavigate = () => {
    if (onNavigate) {
      onNavigate();
    } else if (announcement.linkUrl) {
      window.location.href = announcement.linkUrl;
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // 背景クリックやEscキーでは閉じない（意図しない閉じ防止）
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: isEarlyAccess
                ? 'rgba(255, 152, 0, 0.15)'
                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
            }}
          >
            {isEarlyAccess
              ? <ScienceIcon sx={{ color: '#ff9800', fontSize: '1.2rem' }} />
              : <AutoAwesomeIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
            }
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {announcement.title}
              </Typography>
              {announcement.badge && (
                <Chip
                  label={announcement.badge}
                  size="small"
                  color={announcement.badgeColor || 'primary'}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}
                />
              )}
              {announcement.version && (
                <Chip
                  label={announcement.version}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'text.secondary'
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontSize: '0.75rem',
            lineHeight: 1.25,
            whiteSpace: 'pre-wrap'
          }}
        >
          {announcement.description}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'text.secondary'
              }
            }}
          >
            閉じる
          </Button>
        </Box>
        {(announcement.linkText && (announcement.linkUrl || onNavigate)) && (
          <Button
            onClick={handleNavigate}
            variant="outlined"
            fullWidth
            sx={{
              color: 'text.primary',
              borderColor: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
              }
            }}
          >
            {announcement.linkText}
          </Button>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={() => {
              window.location.href = '/release-notes';
              handleClose();
            }}
            size="small"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 400,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'primary.main'
              }
            }}
          >
            過去のリリースを確認する
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FeatureAnnouncementDialog;
